import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePredictions } from '../hooks/usePredictions'

const TABS = [
  { key: 'alltime', label: 'All Time' },
  { key: 'monthly', label: 'This Month' },
]

export default function Leaderboard() {
  const { user } = useAuth()
  const { predictions } = usePredictions()
  const [tab, setTab] = useState('alltime')
  const [allTimeRows, setAllTimeRows] = useState([])
  const [monthlyRows, setMonthlyRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [username, setUsername] = useState('')

  useEffect(() => {
    Promise.all([
      supabase.from('leaderboard').select('*'),
      supabase.from('leaderboard_monthly').select('*'),
    ]).then(([at, mo]) => {
      setAllTimeRows(at.data || [])
      setMonthlyRows(mo.data || [])
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (user) {
      supabase.from('profiles').select('username').eq('id', user.id).single()
        .then(({ data }) => { if (data?.username) setUsername(data.username) })
    }
  }, [user])

  // Current user stats from their own predictions hook
  const scored = predictions.filter(p => p.is_correct != null && p.actual_change != null)
  const correct = scored.filter(p => p.direction_correct).length
  const hypoReturn = scored.reduce((sum, p) =>
    sum + (p.direction_correct
      ? 10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100
      : -10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100), 0)

  // Current month stats
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const scoredMonth = scored.filter(p => new Date(p.scored_at) >= monthStart)
  const correctMonth = scoredMonth.filter(p => p.direction_correct).length
  const hypoReturnMonth = scoredMonth.reduce((sum, p) =>
    sum + (p.direction_correct
      ? 10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100
      : -10000 * Math.abs(parseFloat(p.actual_change) || 0) / 100), 0)

  const rows = tab === 'alltime' ? allTimeRows : monthlyRows
  const sorted = [...rows].sort((a, b) => Number(b.hypothetical_pnl) - Number(a.hypothetical_pnl))
  const userRank = username ? sorted.findIndex(r => r.username === username) + 1 : 0

  const fmtPnl = (val) => {
    const n = Number(val)
    const abs = Math.abs(n)
    const fmt = abs >= 1000 ? `$${(abs / 1000).toFixed(1)}k` : `$${abs.toFixed(0)}`
    return n >= 0 ? `+${fmt}` : `-${fmt}`
  }

  const monthName = now.toLocaleString('default', { month: 'long' })
  const myHypo = tab === 'alltime' ? hypoReturn : hypoReturnMonth
  const myScored = tab === 'alltime' ? scored.length : scoredMonth.length
  const myCorrect = tab === 'alltime' ? correct : correctMonth
  const myAccuracy = myScored > 0 ? Math.round((myCorrect / myScored) * 100) : 0
  const needsMore = tab === 'alltime' && scored.length < 10

  return (
    <div className="animate-in">
      <style>{`
        @media (max-width: 600px) {
          .lb-title { font-size: 26px !important; }
          .lb-stats-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 14px !important; }
          .lb-stats-grid .stat-username { grid-column: span 2; }
          .lb-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .lb-table { min-width: 420px; }
          .lb-table td, .lb-table th { padding: 10px 12px !important; }
        }
      `}</style>
      <div style={{ marginBottom: 24 }}>
        <h1 className="lb-title" style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>
          Leaderboard
        </h1>
        <p style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
          Ranked by hypothetical return · $10k simulated per prediction
        </p>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', marginBottom: 24, border: '1px solid var(--border)', borderRadius: 5, overflow: 'hidden', width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 24px', border: 'none', cursor: 'pointer',
            background: tab === t.key ? 'var(--olive)' : 'transparent',
            color: tab === t.key ? '#fff' : 'var(--text2)',
            fontWeight: 600, fontSize: 13, fontFamily: 'var(--sans)',
            transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Your stats box */}
      {user && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderTop: '3px solid var(--olive)',
          borderRadius: 6, padding: '20px 24px', marginBottom: 24,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 16 }}>
            Your Stats · {tab === 'monthly' ? monthName : 'All Time'}
          </div>
          <div className="lb-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20 }}>
            {[
              { label: 'Rank', value: userRank > 0 ? `#${userRank}` : '—', color: 'var(--olive)' },
              { label: 'Username', value: username || user.email?.split('@')[0], color: 'var(--text)', small: true, cls: 'stat-username' },
              { label: 'Scored', value: myScored || '—', color: 'var(--text)' },
              { label: '% Correct', value: myScored > 0 ? `${myAccuracy}%` : '—', color: myAccuracy >= 50 ? 'var(--green)' : myScored === 0 ? 'var(--text)' : 'var(--red)' },
              { label: 'Hypo Return', value: myScored > 0 ? fmtPnl(myHypo) : '—', color: myHypo >= 0 ? 'var(--green)' : myScored === 0 ? 'var(--text)' : 'var(--red)' },
            ].map(s => (
              <div key={s.label} className={s.cls || ''}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 8 }}>
                  {s.label}
                </div>
                <div style={{ fontSize: s.small ? 16 : 24, fontWeight: 700, fontFamily: 'var(--mono)', color: s.color, wordBreak: 'break-all' }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
          {needsMore && (
            <div style={{
              marginTop: 16, padding: '10px 14px',
              background: 'var(--surface2)', borderRadius: 4,
              fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)',
            }}>
              {10 - scored.length} more scored prediction{10 - scored.length !== 1 ? 's' : ''} needed to appear on the All Time leaderboard
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' }}>Loading...</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 8 }}>
              {tab === 'monthly' ? 'NO ENTRIES YET THIS MONTH' : 'NO ENTRIES YET'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              {tab === 'alltime' ? 'Make 10+ predictions to appear here' : 'Be the first on the board this month'}
            </div>
          </div>
        ) : (
          <div className="lb-table-wrap"><table className="lb-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
            <thead>
              <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Rank</th>
                <th style={thStyle}>Username</th>
                <th style={thStyle}>Hypo Return</th>
                <th style={thStyle}>Predictions</th>
                <th style={thStyle}>% Correct</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => {
                const pnl = Number(row.hypothetical_pnl)
                const winRate = Number(row.win_rate)
                const isMe = row.username === username
                return (
                  <tr key={row.username} style={{
                    borderBottom: '1px solid var(--border)',
                    background: isMe ? 'var(--olive-bg)' : i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)',
                  }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 13 }}>
                      {i < 3 ? ['🥇', '🥈', '🥉'][i] : <span style={{ color: 'var(--text3)' }}>#{i + 1}</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 13, color: 'var(--olive)' }}>
                      {row.username}{isMe && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>you</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {fmtPnl(pnl)}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text2)' }}>
                      {row.total_predictions}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 12,
                        color: winRate >= 50 ? 'var(--green)' : 'var(--red)',
                      }}>{row.win_rate}%</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  )
}

const thStyle = {
  padding: '11px 16px', textAlign: 'left',
  fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '1px', color: 'var(--text3)',
  fontFamily: 'var(--mono)', whiteSpace: 'nowrap',
}
